import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT, LanguageSwitcher } from "@/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Coins, Gear, Trophy, WifiHigh } from "@phosphor-icons/react";
import AthletesTab from "@/components/tabs/AthletesTab";
import PoolsTab from "@/components/tabs/PoolsTab";
import RingsTab from "@/components/tabs/RingsTab";
import SettingsTab from "@/components/tabs/SettingsTab";
import ResultsTab from "@/components/tabs/ResultsTab";
import OfflineTab from "@/components/tabs/OfflineTab";
import { TokenBanner } from "@/components/TokenBanner";

const TYPE_LABEL = { combat: "Combattimento", poomsae: "Poomsae", mixed: "Misto" };
// Offline tab temporarily hidden (code kept); set true to restore
const SHOW_OFFLINE = false;

export default function EventDetail() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const t = useT();
  const { user, refresh } = useAuth();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("athletes");

  const loadEvent = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setEvent(data);
    } catch (e) {
      if (e.response?.status === 404) nav("/dashboard");
    }
  };
  useEffect(() => {
    loadEvent();
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (!event) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => nav("/dashboard")} data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div>
              <h1 className="text-lg font-bold leading-tight">{event.name}</h1>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="uppercase text-[10px] tracking-widest">{t(`type.${event.type}`)}</Badge>
                {event.my_role && <Badge variant="secondary" className="uppercase text-[10px] tracking-widest text-brand-yellow" data-testid="my-role-badge">{t(`collab.role${event.my_role === "co_organizer" ? "Co" : event.my_role === "registration" ? "Reg" : event.my_role === "observer" ? "Obs" : "Owner"}`)}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card">
              <Coins weight="fill" className="text-gold" size={18} />
              <span className="font-monot text-sm font-semibold tabular" data-testid="event-token-balance">{user?.tokens_balance ?? 0}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6"><TokenBanner /></div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="settings" data-testid="tab-settings" className="px-3" title={t("ev.tabSettings")} aria-label={t("ev.tabSettings")}><Gear size={18} weight="fill" /></TabsTrigger>
            <TabsTrigger value="athletes" data-testid="tab-athletes">{t("ev.tabAthletes")}</TabsTrigger>
            <TabsTrigger value="pools" data-testid="tab-pools">{t("ev.tabPools")}</TabsTrigger>
            <TabsTrigger value="rings" data-testid="tab-rings">{t("ev.tabRings")}</TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results" className="px-3" title={t("ev.tabResults")} aria-label={t("ev.tabResults")}><Trophy size={18} weight="fill" /></TabsTrigger>
            {SHOW_OFFLINE && event.my_role === "owner" && <TabsTrigger value="offline" data-testid="tab-offline" className="px-3" title={t("ev.tabOffline")} aria-label={t("ev.tabOffline")}><WifiHigh size={18} weight="fill" /></TabsTrigger>}
          </TabsList>
          <TabsContent value="athletes" className="mt-6"><AthletesTab eventId={eventId} event={event} /></TabsContent>
          <TabsContent value="pools" className="mt-6"><PoolsTab eventId={eventId} event={event} /></TabsContent>
          <TabsContent value="rings" className="mt-6"><RingsTab eventId={eventId} event={event} /></TabsContent>
          <TabsContent value="results" className="mt-6"><ResultsTab eventId={eventId} event={event} /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsTab eventId={eventId} event={event} onSaved={() => { loadEvent(); setTab("pools"); }} /></TabsContent>
          {SHOW_OFFLINE && event.my_role === "owner" && <TabsContent value="offline" className="mt-6"><OfflineTab eventId={eventId} event={event} onChanged={refresh} /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}
